import { memo } from 'react';
import { Button, Card, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import IntegrationProviderLogo from './IntegrationProviderLogo.jsx';
import { STATUS_DOT_CLASS, statusLabelKey } from '../constants';

/**
 * Marketplace Active Strip (CT-4163): a compact summary of already-connected
 * integrations — a title with a health dot (+ optional "N need attention" note),
 * the connected count and last-update line, up to 5 provider logos each with a
 * health dot whose color comes from the CT-4195 status tokens (single source,
 * `STATUS_DOT_CLASS`), a "+N more" chip when more than five are connected, and a
 * "View all" action.
 *
 * Each logo lifts on hover, is keyboard-focusable, opens Provider Detail via
 * `onOpenProvider`, and carries a `name · status` tooltip. `onViewAll` scrolls to
 * the curated catalog in the prototype; the catalog is CT-4164 and not built yet,
 * so the caller passes a deferred no-op for now.
 */
function ActiveStrip({ summary, onOpenProvider, onViewAll }) {
  const { t } = useTranslation('admin-studio');
  const top = summary?.top ?? [];
  if (!top.length) return null;

  const visible = top.slice(0, 5);
  const connectedCount = summary.connected_count ?? top.length;
  const needsAttention = summary.needs_attention_count ?? 0;
  const lastUpdateMinutes = summary.last_update_minutes;
  const overflow = connectedCount - visible.length;

  return (
    <Card>
      <Card.Body>
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-content">
              <span
                aria-hidden="true"
                className={[
                  'h-2 w-2 rounded-full',
                  needsAttention > 0 ? STATUS_DOT_CLASS.auth_expiring : STATUS_DOT_CLASS.working,
                ].join(' ')}
              />
              {t('integrations.marketplace.activeStrip.title')}
              {needsAttention > 0 && (
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                  {`· ${t('integrations.marketplace.activeStrip.needAttention', { count: needsAttention })}`}
                </span>
              )}
            </div>
            <p className="text-xs text-content-muted">
              {t('integrations.marketplace.activeStrip.connected', { count: connectedCount })}
              {lastUpdateMinutes != null &&
                ` · ${t('integrations.marketplace.activeStrip.lastUpdate', { value: lastUpdateMinutes })}`}
            </p>
          </div>

          <div className="flex flex-shrink-0 items-center gap-3">
            {visible.map((item) => (
              <Tooltip
                key={item.slug}
                content={`${item.name} · ${t(statusLabelKey(item.status))}`}
                position="bottom"
                size="compact"
                arrow={false}
              >
                <button
                  type="button"
                  onClick={() => onOpenProvider?.(item.slug)}
                  aria-label={t('integrations.card.openAria', { name: item.name })}
                  className="relative rounded-lg transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
                >
                  <IntegrationProviderLogo slug={item.slug} name={item.name} size="small" />
                  <span
                    aria-hidden="true"
                    className={[
                      'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border border-surface',
                      STATUS_DOT_CLASS[item.status] ?? 'bg-gray-400',
                    ].join(' ')}
                  />
                </button>
              </Tooltip>
            ))}
            {overflow > 0 && (
              <span className="rounded-md border border-edge bg-surface-muted px-2 py-1 text-xs font-medium text-content-muted">
                {t('integrations.marketplace.activeStrip.more', { count: overflow })}
              </span>
            )}
            <Button variant="tertiary" size="sm" onClick={onViewAll}>
              {t('integrations.marketplace.activeStrip.viewAll')}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default memo(ActiveStrip);
